/**
 * Reading one line of arrow notation — the text syntax shared by the flowchart and the
 * concept map.
 *
 * A line is a chain of items joined by arrows: `Plants -produce-> Oxygen -> Respiration`.
 * An arrow is `->`, `-->`, `=>` or `→`, and may carry a label in any of three spellings:
 * `-label->` (no spaces next to the dashes), `-- label -->`, or `-->|label|`. The label
 * forms need a space before the first dash, so a hyphenated word such as `Pre-process`
 * is never mistaken for one.
 *
 * A line that *starts* with an arrow continues from the line above it: the first item of
 * that line is whatever the previous chain started from. The caller decides what that
 * means; this module only reports it.
 */

export interface ArrowToken {
  label: string;
}

export interface ChainLine {
  /** Set when the line begins with an arrow. */
  lead: ArrowToken | null;
  items: string[];
  /** `arrows[i]` joins `items[i]` to `items[i + 1]`. */
  arrows: ArrowToken[];
  /** Empty items between arrows, or an arrow with nothing after it. */
  empty: number;
}

const ARROW =
  /(?:(?<=^|\s)(?:--\s+(.+?)\s+-{2,}>|-([^\s>-](?:[^>]*?[^\s-])?)-{1,2}>|-{1,3}>\s*\|([^|]*)\|))|-{1,3}>|=>|→/g;

const WHITESPACE = /\s+/g;

/** Words and shapes are compared without case, spacing or surrounding quotes. */
export function nodeKey(text: string): string {
  return text.replace(WHITESPACE, " ").trim().toLowerCase();
}

/** Split a line into its items and the arrows between them. */
export function parseChainLine(line: string): ChainLine {
  const found: { index: number; length: number; label: string }[] = [];
  for (const match of line.matchAll(ARROW)) {
    found.push({
      index: match.index ?? 0,
      length: match[0].length,
      label: (match[1] ?? match[2] ?? match[3] ?? "").trim(),
    });
  }
  if (found.length === 0) {
    const text = line.trim();
    return {
      lead: null,
      items: text === "" ? [] : [text],
      arrows: [],
      empty: 0,
    };
  }
  const segments: string[] = [];
  let cursor = 0;
  for (const arrow of found) {
    segments.push(line.slice(cursor, arrow.index).trim());
    cursor = arrow.index + arrow.length;
  }
  segments.push(line.slice(cursor).trim());

  let lead: ArrowToken | null = null;
  let arrows = found.map((arrow) => ({ label: arrow.label }));
  if (segments[0] === "") {
    lead = arrows[0];
    arrows = arrows.slice(1);
    segments.shift();
  }
  const items: string[] = [];
  const joins: ArrowToken[] = [];
  let empty = 0;
  for (const [i, segment] of segments.entries()) {
    if (segment === "") {
      empty += 1;
      continue;
    }
    if (items.length > 0) {
      joins.push(arrows[i - 1] ?? { label: "" });
    }
    items.push(segment);
  }
  return { lead, items, arrows: joins, empty };
}
